'use client';

import { useEffect, useRef, useState, useTransition } from 'react';

import { usePathname } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { track } from '@vercel/analytics';
import { MessageCircle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@kit/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { PhoneInput } from '@kit/ui/phone-input';
import { cn } from '@kit/ui/utils';

import {
  BUSINESS_TYPES,
  type BusinessType,
  CONSENT_WAIT_MS,
  CONTENT_SCROLL_DEPTH,
  type LeadPromptTrigger,
  PAID_PROMPT_DELAY_MS,
  defaultBusinessType,
  isLeadPromptSuppressed,
  isPaidVisit,
  leadPromptOffer,
  leadPromptTrigger,
  parseLeadPromptMemory,
} from '~/lib/marketing/lead-capture/lead-prompt';
import { parseAttribution } from '~/lib/marketing/tracking/attribution';

import { BUSINESS_TYPE_LABELS, QUICK_LEAD_FORM, QUICK_LEAD_OFFERS } from '../../_lib/quick-lead.content';
import { QuickLeadSchema } from '../../_lib/quick-lead.schema';
import { sendQuickLead } from '../../_lib/server/marketing-contact-actions';
import { SchedulingModal } from '../google-calendar-button';
import { useMarketingTracking } from '../tracking/marketing-tracking-context';

const MEMORY_KEY = 'af_lead_prompt';
const PAID_SESSION_KEY = 'af_paid_session';
const MIN_PROMPT_DELAY_MS = 1_500;

/** Also called by the main contact form, so a converted visitor is never prompted. */
export function rememberLeadPrompt(state: 'dismissed' | 'submitted') {
  try {
    window.localStorage.setItem(MEMORY_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch {
    // Storage blocked (private mode): the prompt may show again next visit.
  }
}

function isPromptSuppressed(): boolean {
  try {
    return isLeadPromptSuppressed(parseLeadPromptMemory(window.localStorage.getItem(MEMORY_KEY)), Date.now());
  } catch {
    return false;
  }
}

/**
 * Click ids only exist on the landing URL; client navigation drops them, so
 * the paid flag is kept for the browser session.
 */
function detectPaidSession(): boolean {
  const paidNow = isPaidVisit(parseAttribution(window.location.href, '', Date.now()));

  try {
    if (paidNow) window.sessionStorage.setItem(PAID_SESSION_KEY, '1');

    return paidNow || window.sessionStorage.getItem(PAID_SESSION_KEY) === '1';
  } catch {
    return paidNow;
  }
}

function scrollDepth(): number {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;

  return scrollable <= 0 ? 0 : window.scrollY / scrollable;
}

type PromptState =
  | { phase: 'waiting' }
  | { phase: 'open'; trigger: LeadPromptTrigger }
  | { phase: 'closed' };

/**
 * Quick WhatsApp lead capture: name, number and business type. Opens a few
 * seconds after an ad click, or halfway down a guide or comparison page for
 * organic readers. At most once per page load, rested for a week after a
 * dismissal and never again after a submission.
 */
export function QuickLeadPrompt({
  consentDecided,
  onOpenChange,
}: {
  consentDecided: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const pathname = usePathname();
  const paidSession = useRef<boolean | null>(null);
  // Deadlines count from landing, so client navigation never restarts them.
  const landedAt = useRef<number | null>(null);
  const [prompt, setPrompt] = useState<PromptState>({ phase: 'waiting' });

  // Timers and the scroll listener are external events, hence an effect.
  useEffect(() => {
    if (prompt.phase !== 'waiting') return;

    // The first run sees the landing URL, before any client navigation.
    paidSession.current ??= detectPaidSession();
    landedAt.current ??= Date.now();

    const trigger = leadPromptTrigger(pathname, paidSession.current);

    if (!trigger || isPromptSuppressed()) return;

    const open = () => {
      setPrompt({ phase: 'open', trigger });
      onOpenChange(true);
      track('marketing_lead_prompt_shown', { trigger });
    };

    if (trigger === 'paid') {
      const deadline = landedAt.current + (consentDecided ? PAID_PROMPT_DELAY_MS : CONSENT_WAIT_MS);
      // A short pause after a just-made consent choice, so the two never collide.
      const timer = window.setTimeout(open, Math.max(MIN_PROMPT_DELAY_MS, deadline - Date.now()));

      return () => window.clearTimeout(timer);
    }

    const onScroll = () => {
      if (scrollDepth() >= CONTENT_SCROLL_DEPTH) open();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Restored scroll positions are already past the threshold.
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, consentDecided, prompt.phase, onOpenChange]);

  if (prompt.phase !== 'open') return null;

  const close = (submitted: boolean) => {
    if (!submitted) {
      rememberLeadPrompt('dismissed');
      track('marketing_lead_prompt_dismissed', { trigger: prompt.trigger });
    }

    setPrompt({ phase: 'closed' });
    onOpenChange(false);
  };

  return <QuickLeadDialog trigger={prompt.trigger} pathname={pathname} onClose={close} />;
}

function QuickLeadDialog({
  trigger,
  pathname,
  onClose,
}: {
  trigger: LeadPromptTrigger;
  pathname: string;
  onClose: (submitted: boolean) => void;
}) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';
  const offer = QUICK_LEAD_OFFERS[leadPromptOffer(pathname)];
  const f = QUICK_LEAD_FORM;
  const { trackLead } = useMarketingTracking();
  const [pending, startTransition] = useTransition();
  const [status, setStatus] = useState<'idle' | 'error' | 'success' | 'scheduling'>('idle');

  const form = useForm({
    resolver: zodResolver(QuickLeadSchema),
    defaultValues: {
      name: '',
      whatsapp: '',
      businessType: defaultBusinessType(pathname),
      website: '' as const,
    },
  });
  const errors = form.formState.errors;

  if (status === 'scheduling') {
    return <SchedulingModal open onClose={() => onClose(true)} />;
  }

  const submitted = status === 'success';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose(submitted)}>
      <DialogContent
        hideCloseButton
        data-test="quick-lead-prompt"
        className={cn(
          'gap-0 overflow-hidden border-[#dcd7cf] bg-[#f7f6f4] p-0 text-[#202020]',
          // Bottom sheet on phones, centred card from the sm breakpoint.
          'top-auto bottom-0 start-0 max-h-[100dvh] max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-t-3xl rtl:translate-x-0',
          'sm:top-[50%] sm:bottom-auto sm:start-[50%] sm:max-w-[460px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:rtl:-translate-x-[-50%]',
        )}
      >
        <div className="relative bg-[#202020] px-6 pt-6 pb-5 text-white">
          <DialogClose
            aria-label={f.close[locale]}
            className="absolute top-4 end-4 cursor-pointer rounded-full p-1 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </DialogClose>
          <p className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 font-roboto-mono text-[11px] font-bold tracking-wide uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" aria-hidden="true" />
            {offer.eyebrow[locale]}
          </p>
          <DialogTitle className="mt-3 pe-6 font-roboto text-[22px] leading-[1.25] font-normal sm:text-[24px]">
            {submitted ? f.success.heading[locale] : offer.heading[locale]}
          </DialogTitle>
          <DialogDescription className="mt-2 font-roboto text-[14px] leading-[1.5] text-white/75">
            {submitted ? f.success.body[locale] : offer.body[locale]}
          </DialogDescription>
        </div>

        {submitted ? (
          <div className="px-6 py-5">
            <button
              type="button"
              data-test="quick-lead-book-call"
              onClick={() => setStatus('scheduling')}
              className="h-12 w-full cursor-pointer bg-[#202020] font-roboto-mono text-[13px] font-bold text-white uppercase transition-colors hover:bg-[#2b2b2b]"
            >
              {f.success.book[locale]}
            </button>
          </div>
        ) : (
          <Form {...form}>
            <form
              className="flex flex-col gap-4 px-6 py-5"
              onSubmit={form.handleSubmit((data) => {
                startTransition(async () => {
                  const eventId = crypto.randomUUID();

                  try {
                    await sendQuickLead({ ...data, eventId, trigger, pagePath: pathname });
                    trackLead(eventId);
                    track('marketing_quick_lead_submitted', { trigger, businessType: data.businessType });
                    rememberLeadPrompt('submitted');
                    setStatus('success');
                  } catch {
                    track('marketing_quick_lead_failed', { trigger });
                    setStatus('error');
                  }
                });
              })}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.name[locale]}</FormLabel>
                      <FormControl>
                        <Input autoComplete="name" maxLength={200} className="bg-white" {...field} />
                      </FormControl>
                      {errors.name ? <FieldError>{f.errors.name[locale]}</FieldError> : null}
                    </FormItem>
                  )}
                />
                <FormField
                  name="whatsapp"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.whatsapp[locale]}</FormLabel>
                      <FormControl>
                        <PhoneInput
                          autoComplete="tel"
                          inputClassName="bg-white"
                          data-test="quick-lead-whatsapp"
                          {...field}
                        />
                      </FormControl>
                      {errors.whatsapp ? <FieldError>{f.errors.whatsapp[locale]}</FieldError> : null}
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                name="businessType"
                render={({ field }) => (
                  <FormItem>
                    <p id="quick-lead-business-label" className="text-sm font-medium">
                      {f.businessType[locale]}
                    </p>
                    <BusinessTypeChips
                      value={field.value}
                      onChange={field.onChange}
                      locale={locale}
                      invalid={Boolean(errors.businessType)}
                    />
                    {errors.businessType ? (
                      <FieldError id="quick-lead-business-error">{f.errors.businessType[locale]}</FieldError>
                    ) : null}
                  </FormItem>
                )}
              />

              {/* Honeypot: hidden from real users, catches naive bots */}
              <FormField
                name="website"
                render={({ field }) => (
                  <div className="hidden" aria-hidden="true">
                    <Input tabIndex={-1} autoComplete="off" {...field} />
                  </div>
                )}
              />

              {status === 'error' ? <FieldError>{f.errors.submit[locale]}</FieldError> : null}

              <button
                type="submit"
                disabled={pending}
                data-test="quick-lead-submit"
                className="inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 bg-[#202020] font-roboto-mono text-[13px] font-bold text-white uppercase transition-colors hover:bg-[#2b2b2b] disabled:opacity-60"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" aria-hidden="true" />
                {f.submit[locale]}
              </button>

              <div className="flex items-start justify-between gap-4 font-roboto text-[12px] leading-[1.5] text-[#6b665e]">
                <p>
                  {f.privacyNote[locale]}{' '}
                  <a href="/privacy-policy" className="underline underline-offset-4">
                    {f.privacy[locale]}
                  </a>
                </p>
                <DialogClose className="shrink-0 cursor-pointer underline-offset-4 hover:underline">
                  {f.dismiss[locale]}
                </DialogClose>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * WAI-ARIA radio group: one Tab stop (the selected chip, or the first),
 * arrow keys move and select, matching native radio buttons.
 */
function BusinessTypeChips({
  value,
  onChange,
  locale,
  invalid,
}: {
  value: BusinessType | undefined;
  onChange: (value: BusinessType) => void;
  locale: 'en' | 'ar';
  invalid: boolean;
}) {
  const tabStop = value ?? BUSINESS_TYPES[0];

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let step = keys[event.key];

    if (!step) return;

    event.preventDefault();
    if (document.dir === 'rtl' && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) step = -step;

    const index = BUSINESS_TYPES.indexOf(tabStop);
    const next = BUSINESS_TYPES[(index + step + BUSINESS_TYPES.length) % BUSINESS_TYPES.length]!;

    onChange(next);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-value="${next}"]`)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby="quick-lead-business-label"
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? 'quick-lead-business-error' : undefined}
      onKeyDown={onKeyDown}
      className="flex flex-wrap gap-2"
    >
      {BUSINESS_TYPES.map((type) => {
        const selected = value === type;

        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={type === tabStop ? 0 : -1}
            data-value={type}
            data-test={`quick-lead-business-${type}`}
            onClick={() => onChange(type)}
            className={cn(
              'h-9 cursor-pointer rounded-full border px-3.5 font-roboto text-[13px] transition-colors',
              selected
                ? 'border-[#202020] bg-[#202020] text-white'
                : 'border-[#dcd7cf] bg-white text-[#202020] hover:border-[#202020]',
            )}
          >
            {BUSINESS_TYPE_LABELS[type][locale]}
          </button>
        );
      })}
    </div>
  );
}

function FieldError({ id, children }: React.PropsWithChildren<{ id?: string }>) {
  return (
    <p id={id} role="alert" className="font-roboto text-[13px] text-[#b42318]">
      {children}
    </p>
  );
}
